import {Col, DatePicker, Divider, Form, InputNumber, message, Modal, Row, Switch, Upload} from "antd";
import {InboxOutlined} from "@ant-design/icons";
import {HttpService} from "../../../services/http/Http.service";
import React, {useState} from "react";
import type {UploadFile} from "antd/es/upload/interface";
import {HttpErrorService} from "../../../services/http/HttpError.service";
import {Person} from "../../../model/models";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import dayjs from "dayjs";
import AddressForm from "./components/AddressForm";
import CommonFormFields from "./components/CommonFormFields";
import {useProgressContext} from "../../../services/ProgressContext";

interface Props {
    visible: boolean,
    onClose: () => void,
    simulatorId?: number,
    refreshInstallments: () => Promise<void>,
    protocolNumber?: string,
    protocolDate?: string,
    dataNotifica: string,
    praticeType: string,
    refreshPractices?: (() => void) | undefined,
    person: Person,
    refreshDocuments?: (() => (void | undefined)) | undefined
}

const {Dragger} = Upload;

const disabledDate = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf('day');
};

export default function InstallmentsModal({
                                              visible,
                                              onClose,
                                              simulatorId,
                                              refreshInstallments,
                                              protocolNumber,
                                              protocolDate,
                                              dataNotifica,
                                              praticeType,
                                              refreshPractices,
                                              person,
                                              refreshDocuments
                                          }: Props) {
    const [installmentsForm] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [autoFillAddress, setAutoFillAddress] = useState<boolean>(false);
    const disableAddressFields = autoFillAddress;
    const {startPolling} = useProgressContext();


    const handleOnFinish = async () => {
        const values = installmentsForm.getFieldsValue();
        setUploading(true);

        if (!fileList || fileList.length === 0) {
            message.error("Devi allegare almeno un file per applicare la rateizzazione.");
            setUploading(false);
            return;
        }

        try {
            let transmittedVia;
            if (values.transmissionType === "email") {
                transmittedVia = "email: " + values.email;
            } else if (values.transmissionType === "pec") {
                transmittedVia = "PEC: " + values.email;
            } else {
                transmittedVia = values.email;
            }

            const rateizzazioneTemplateDto = {
                name: person?.firstName!,
                surname: person?.lastName!,
                address: values.address,
                cap: values.cap,
                city: values.city,
                province: values.province,
                email: transmittedVia,
                numberProtocolRequest: values.requestProtocolNumber,
                archivingProtocol: protocolNumber!,
                dataArchiving: protocolDate!,
                rateizzazioneDate: values.requestProtocolDate.format("DD-MM-YYYY"),
                installmentNumber: values.installmentNumber,
                firstInstallmentDate: values.firstInstallmentDate.format("DD-MM-YYYY"),
                dataNotifica: dataNotifica,
                practiceType: praticeType,
                residenceHouseNumber: values.houseNumber
            };

            const recalculateInstallmentPlanRequest = {
                installmentsNumber: values.installmentNumber,
                firstDeadLine: values.firstInstallmentDate.format("YYYY-MM-DD"),
                applyInterests: values.applyInterests || false,
                rateizzazioneTemplateDto: rateizzazioneTemplateDto
            };

            const formData = new FormData();
            formData.append("request", new Blob([JSON.stringify(recalculateInstallmentPlanRequest)], {type: "application/json"}));

            fileList.forEach((file) => {
                if (file.originFileObj) {
                    formData.append("file", file.originFileObj);
                }
            });

            const data = await HttpService.getInstanceAxios().post(
                `/file-ts/simulator/${simulatorId}/person/${person?.id}/reCalculateInstallmentPlan`,
                formData,
            );
            setTimeout(function () {
                startPolling(data.data, () => {
                    async function onCompletedInstallmentRecalculation() {
                        message.success("Rateizzazione applicata e documento generato con successo.");
                        installmentsForm.resetFields();
                        setFileList([]);
                        onClose();
                        setUploading(false);

                        await refreshInstallments()
                        if (refreshPractices) {
                            refreshPractices()
                        }
                        if (refreshDocuments) {
                            refreshDocuments()
                        }

                    }

                    onCompletedInstallmentRecalculation();
                }, (errorData) => {
                    message.error(errorData?.errorMessage || "Errore durante la rateizzazione.");
                    setUploading(false);
                }, true)
            }, 500);

        } catch (error) {
            setUploading(false);
            HttpErrorService.handle("Errore durante la generazione del template della rateizzazione.", error, true)
        }
    };

    return (
        <>
            <Modal
                width={1000}
                title="Rateizzazione"
                open={visible}
                onCancel={onClose}
                destroyOnClose={false}
                onOk={() => {
                    installmentsForm
                        .validateFields()
                        .then(() => handleOnFinish())
                        .catch((info) => {
                            console.log("Errore di validazione:", info);
                        });
                }}
                okText="Applica rateizzazione e genera PagoPA"
                maskClosable={false}
            >
                <Form form={installmentsForm} layout="vertical">
                    <Form.Item
                        label="Richiesta rateizzazione"
                        name="installmentRequest"
                        rules={[
                            {required: true, message: "Inserisci la richiesta di rateizzazione!"}
                        ]}
                    >
                        <Dragger
                            multiple
                            beforeUpload={() => false}
                            onChange={({fileList}) => setFileList(fileList)}
                            fileList={fileList}
                            onRemove={(file) => {
                                setFileList(prev => prev.filter(f => f.uid !== file.uid));
                            }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined/>
                            </p>
                            <p className="ant-upload-text">Clicca o trascina uno o più file</p>
                        </Dragger>
                    </Form.Item>
                    <Row gutter={10}>
                        <Col span={4}>
                            <Form.Item
                                label="Numero di rate"
                                name="installmentNumber"
                                rules={[
                                    {required: true, message: "Inserisci il numero di rate da applicare!"},
                                    {
                                        validator: (_, value) =>
                                            typeof value === "number"
                                                ? Promise.resolve()
                                                : Promise.reject("Inserisci un numero valido!")
                                    },
                                    {
                                        validator: (_, value) =>
                                            value <= 30
                                                ? Promise.resolve()
                                                : Promise.reject("Il numero di rate non può superare 30!")
                                    }
                                ]}
                            >
                                <InputNumber style={{width:"auto"}} min={2} max={30}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label="Data scadenza prima rata"
                                name="firstInstallmentDate"
                                rules={[
                                    {required: true, message: "Inserisci la data della scadenza della prima rata!"}
                                ]}
                            >
                                <DatePicker style={{width:200}} format={"DD-MM-YYYY"} disabledDate={disabledDate}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="applyInterests" valuePropName="checked" label={"Applicare gli interessi?"}>
                        <Switch defaultChecked={false}></Switch>
                    </Form.Item>
                    <Divider>Informazioni per l'invio</Divider>

                    <CommonFormFields fieldsToShow={["requestProtocolNumber", "requestProtocolDate", "email"]}/>

                    <AddressForm
                        autoFillAddress={autoFillAddress}
                        setAutoFillAddress={setAutoFillAddress}
                        disableAddressFields={disableAddressFields}
                        form={installmentsForm}
                        personCF={person.fiscalCode}
                    />
                </Form>
            </Modal>
            <CustomSpinner
                message={"Generazione PagoPa in corso..."}
                open={uploading}
            />
        </>
    );
}
